import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  getDocs, 
  addDoc, 
  serverTimestamp, 
  orderBy,
  where 
} from 'firebase/firestore';
import { db } from '../../firebase';
import { sanitizeEmail, sanitizeFormObject, sanitizePhone, sanitizeText } from '../../utils/inputSanitizer';
import { createAdminNotificationMeta } from '../../utils/adminNotificationMeta';
import { useAuth } from '../contexts/AuthContext';
import SEO from '../seo/SEO';

const Careers = () => {
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [resumeError, setResumeError] = useState('');
  const { user } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    coverLetter: '',
    portfolioLink: '',
    linkedinProfile: ''
  });

  // SEO data
  const seoData = {
    title: "Careers - Intellectual Oasis Fellowship",
    description: "Join our team at Intellectual Oasis Fellowship. Explore current job openings and apply today to be part of our mission to transform education.",
    keywords: "careers, jobs, employment, education jobs, Intellectual Oasis Fellowship careers",
    image: "/images/careers-og.jpg"
  };

  // Load career positions
  useEffect(() => {
    const fetchPositions = async () => {
      setLoading(true);
      try {
        console.log("Fetching positions...");
        const positionsQuery = query(
          collection(db, "career_positions"),
          where("isActive", "==", true),
          orderBy("postedDate", "desc")
        );
  
        const positionsSnapshot = await getDocs(positionsQuery);
  
        if (positionsSnapshot.empty) {
          console.log("No matching documents.");
        } else {
          console.log("Documents fetched:", positionsSnapshot.size);
        }
  
        const positionsList = positionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
  
        console.log("Positions list:", positionsList);
        setPositions(positionsList);
      } catch (error) {
        console.error("Error fetching positions:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchPositions();
  }, []);
  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const cleanedValue =
      name === 'email'
        ? sanitizeEmail(value)
        : name === 'phone'
        ? sanitizePhone(value)
        : sanitizeText(value);
    setFormData(prev => ({ ...prev, [name]: cleanedValue }));
  };

  // Handle resume file upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setResumeFile(null);
      setResumeError('');
      return;
    }

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setResumeError('Please upload a PDF or Word document');
      setResumeFile(null);
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setResumeError('File size must be less than 5MB');
      setResumeFile(null);
      return;
    }

    setResumeFile(file);
    setResumeError('');
  };

  // Upload file to Cloudinary
  const uploadToCloudinary = async (file) => {
    const cloudinaryUrl = process.env.REACT_APP_CLOUDINARY_UPLOAD_URL;
    const cloudinaryPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;
    
    if (!cloudinaryUrl || !cloudinaryPreset) {
      throw new Error('Cloudinary configuration missing');
    }
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryPreset);
    
    const response = await fetch(cloudinaryUrl, {
      method: 'POST',
      body: formData
    });
    
    if (!response.ok) {
      throw new Error('File upload failed');
    }
    
    const data = await response.json();
    return data.secure_url;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!resumeFile) {
      setResumeError('Please upload your resume');
      return;
    }

    if (!user) {
      setFormError('You must be logged in to apply. Please sign in or create an account.');
      return;
    }

    setFormSubmitting(true);
    setFormError('');
    
    try {
      // Upload resume to Cloudinary
      const resumeURL = await uploadToCloudinary(resumeFile);
      
      const sanitizedApplication = sanitizeFormObject(formData);
      // Save application to Firestore
      await addDoc(collection(db, "career_applications"), {
        userId: user.uid,
        positionId: selectedPosition.id,
        positionTitle: selectedPosition.title,
        fullName: sanitizedApplication.fullName,
        email: sanitizedApplication.email,
        phone: sanitizedApplication.phone,
        coverLetter: sanitizedApplication.coverLetter,
        portfolioLink: sanitizedApplication.portfolioLink,
        linkedinProfile: sanitizedApplication.linkedinProfile,
        resumeURL,
        status: 'new',
        appliedAt: serverTimestamp(),
        ...createAdminNotificationMeta({
          type: 'career_application',
          summary: `New career application for ${selectedPosition.title} from ${sanitizedApplication.fullName}`,
        }),
      });
      
      // Success!
      setFormSuccess(true);
      
      // Reset form after submission
      setTimeout(() => {
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          coverLetter: '',
          portfolioLink: '',
          linkedinProfile: ''
        });
        setResumeFile(null);
        setShowApplicationForm(false);
        setFormSuccess(false);
      }, 5000);
      
    } catch (error) {
      console.error("Error submitting application:", error);
      setFormError('An error occurred while submitting your application. Please try again.');
    }
    
    setFormSubmitting(false);
  };

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Start application
  const handleStartApplication = (position) => {
    setSelectedPosition(position);
    setShowApplicationForm(true);
    
    // Populate form with user data if available
    if (user) {
      setFormData(prev => ({
        ...prev,
        fullName: user.displayName || '',
        email: user.email || ''
      }));
    }
    
    // Scroll to form
    setTimeout(() => {
      document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <>
      <SEO {...seoData} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Join Our Team</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Be part of our mission to transform education and empower the next generation of leaders
          </p>
        </div>

        {/* Why Join Us */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Join Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">🌱</div>
              <h3 className="text-xl font-semibold mb-2">Growth Opportunities</h3>
              <p className="text-gray-600">We foster a culture of continuous learning and professional development.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">🌍</div>
              <h3 className="text-xl font-semibold mb-2">Meaningful Impact</h3>
              <p className="text-gray-600">Make a real difference in education and help shape the future.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg text-center">
              <div className="text-4xl mb-4">👥</div>
              <h3 className="text-xl font-semibold mb-2">Collaborative Culture</h3>
              <p className="text-gray-600">Work with talented individuals in an inclusive and supportive environment.</p>
            </div>
          </div>
        </div>

        {/* Current Openings */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Current Openings</h2>
          
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[rgb(130,88,18)]"></div>
            </div>
          ) : positions.length > 0 ? (
            <div className="grid grid-cols-1 gap-6">
              {positions.map(position => (
                <div key={position.id} className="bg-white p-6 rounded-lg shadow-lg">
                  <div className="flex flex-col md:flex-row justify-between">
                    <div>
                      <h3 className="text-2xl font-semibold mb-2">{position.title}</h3>
                      <div className="mb-4 flex flex-wrap gap-4">
                        <span className="inline-flex items-center text-sm text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {position.location || 'Remote'}
                        </span>
                        <span className="inline-flex items-center text-sm text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          {position.type || 'Full-time'}
                        </span>
                        <span className="inline-flex items-center text-sm text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Posted: {formatDate(position.postedDate)}
                        </span>
                      </div>
                    </div>
                    <div className="mt-4 md:mt-0">
                      <button
                        onClick={() => handleStartApplication(position)}
                        className="px-6 py-3 bg-[rgb(130,88,18)] text-white rounded-full font-medium hover:bg-[rgb(110,68,0)] transition-colors duration-300"
                      >
                        Apply Now
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-4">
                    <div className="prose max-w-none mb-4">
                      <h4 className="text-lg font-medium mb-2">Description</h4>
                      <p>{position.description}</p>
                    </div>
                    
                    {position.responsibilities && (
                      <div className="mb-4">
                        <h4 className="text-lg font-medium mb-2">Responsibilities</h4>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700">
                          {position.responsibilities.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {position.qualifications && (
                      <div>
                        <h4 className="text-lg font-medium mb-2">Qualifications</h4>
                        <ul className="list-disc pl-5 space-y-1 text-gray-700">
                          {position.qualifications.map((item, index) => (
                            <li key={index}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-2">No Open Positions</h3>
              <p className="text-gray-600 mb-4">
                We don't have any open positions at the moment. Please check back later or send us your resume for future opportunities.
              </p>
              <a 
                href="mailto:careers@iofellowship.org" 
                className="text-[rgb(130,88,18)] hover:underline"
              >
                careers@iofellowship.org
              </a>
            </div>
          )}
        </div>

        {/* Application Form */}
        {showApplicationForm && selectedPosition && (
          <div id="application-form" className="mb-16 bg-white p-8 rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Apply for: {selectedPosition.title}
            </h2>
            <p className="text-gray-600 mb-6">Please fill out the form below to apply for this position.</p>
            
            {!user && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      You must be logged in to apply. Please <a href="/login" className="font-medium underline">sign in</a> or <a href="/register" className="font-medium underline">create an account</a>.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {formSuccess ? (
              <div className="bg-green-50 border-l-4 border-green-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      Application submitted successfully! We'll be in touch soon.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {formError && (
                  <div className="bg-red-50 border-l-4 border-red-400 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm text-red-700">{formError}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    inputMode="tel"
                    pattern="[0-9+\-() ]*"
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Resume/CV</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    required
                  />
                  {resumeError && <p className="mt-1 text-sm text-red-600">{resumeError}</p>}
                  <p className="mt-1 text-xs text-gray-500">Please upload a PDF or Word document (5MB max)</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cover Letter</label>
                  <textarea
                    name="coverLetter"
                    value={formData.coverLetter}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                    required
                  ></textarea>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Portfolio Link (Optional)</label>
                    <input
                      type="url"
                      name="portfolioLink"
                      value={formData.portfolioLink}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      placeholder="https://yourportfolio.com"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">LinkedIn Profile (Optional)</label>
                    <input
                      type="url"
                      name="linkedinProfile"
                      value={formData.linkedinProfile}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[rgb(130,88,18)]"
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end mt-8">
                  <button
                    type="button"
                    onClick={() => setShowApplicationForm(false)}
                    className="px-6 py-3 border border-gray-300 rounded-md mr-4 hover:bg-gray-50 transition-colors duration-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={formSubmitting || !user}
                    className="px-6 py-3 bg-[rgb(130,88,18)] text-white rounded-md font-medium hover:bg-[rgb(110,68,0)] transition-colors duration-300 disabled:opacity-50"
                  >
                    {formSubmitting ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {/* Values Section */}
        <div className="text-center bg-[rgb(130,88,18)] text-white p-8 rounded-lg">
          <h2 className="text-2xl font-bold mb-4">Our Values</h2>
          <p className="mb-6 max-w-2xl mx-auto">
            At Intellectual Oasis Fellowship, we value innovation, inclusivity, integrity, and commitment to excellence. 
            We're looking for passionate individuals who share our vision for transformative education.
          </p>
          {!showApplicationForm && positions.length > 0 && (
            <button
              onClick={() => handleStartApplication(positions[0])}
              className="bg-white text-[rgb(130,88,18)] px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors duration-300"
            >
              Join Our Team
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Careers; 